"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "./utils";
import { buttonVariants } from "./button";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-4", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-4",
        month: "flex flex-col gap-4 backdrop-blur-sm",
        caption: "flex justify-center pt-2 pb-3 relative items-center w-full backdrop-blur-sm rounded-lg",
        caption_label: "text-sm font-semibold font-['SF_Pro_Display',_-apple-system,_sans-serif]",
        nav: "flex items-center gap-1",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "size-8 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm p-0 opacity-70 hover:opacity-100 border-gray-200/50 dark:border-gray-700/50 shadow-sm hover:shadow-md transition-all",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-x-1",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-10 font-medium text-[0.75rem] uppercase tracking-wider font-['SF_Pro_Text',_-apple-system,_sans-serif]",
        row: "flex w-full mt-1",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
          "[&:has([aria-selected])]:bg-purple-100/50 dark:[&:has([aria-selected])]:bg-purple-900/30",
          "[&:has([aria-selected])]:backdrop-blur-sm",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-lg [&:has(>.day-range-start)]:rounded-l-lg first:[&:has([aria-selected])]:rounded-l-lg last:[&:has([aria-selected])]:rounded-r-lg"
            : "[&:has([aria-selected])]:rounded-lg",
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "size-10 p-0 font-medium aria-selected:opacity-100 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 hover:backdrop-blur-sm transition-all font-['SF_Pro_Text',_-apple-system,_sans-serif]",
        ),
        day_range_start:
          "day-range-start aria-selected:bg-purple-500/90 aria-selected:backdrop-blur-md aria-selected:text-white aria-selected:shadow-lg",
        day_range_end:
          "day-range-end aria-selected:bg-purple-500/90 aria-selected:backdrop-blur-md aria-selected:text-white aria-selected:shadow-lg",
        day_selected:
          "bg-purple-500/90 backdrop-blur-md text-white shadow-lg hover:bg-purple-600/90 focus:bg-purple-600/90",
        day_today: "bg-blue-100/80 dark:bg-blue-900/40 backdrop-blur-sm text-accent-foreground font-bold ring-2 ring-blue-400/50 dark:ring-blue-500/50",
        day_outside:
          "day-outside text-muted-foreground/40 aria-selected:text-muted-foreground/60",
        day_disabled: "text-muted-foreground opacity-30",
        day_range_middle:
          "aria-selected:bg-purple-100/50 dark:aria-selected:bg-purple-900/30 aria-selected:backdrop-blur-sm aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn("size-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn("size-4", className)} {...props} />
        ),
      }}
      {...props}
    />
  );
}

export { Calendar };
